<template>
  <v-container fluid>
    <!-- <div>
      <h2>{{ isNew ? 'New Clinic Profile' : 'Clinic Profile Details' }}</h2>
    </div> -->
    <HeaderLabel class="mb-2" :title="isNew ? 'New Clinic Profile' : 'Clinic Profile Details'" />
    <v-container fluid class="custom-container-box">
      <!-- Button-based horizontal tabs -->
      <div class="tab-buttons-container flex-gap">
        <v-btn
          class="flex"
          v-for="tab in tabs"
          :key="tab.id"
          @click="activeTab = tab.id"
          :class="['', { primary: activeTab === tab.id }]"
          elevation="0"
        >
          {{ tab.label }}
        </v-btn>
      </div>
      <v-divider class="my-4" />

      <!-- Tab Content -->
      <div class="tab-content">
        <!-- Details Tab -->
        <div v-if="activeTab === 0">
          <ValidationObserver ref="form" v-slot="{ invalid }">
            <v-card v-if="isNew || formData" flat>
              <v-row class="mr-3 ml-3">
                <v-col class="ml-auto" md="auto">
                  <v-btn :disabled="invalid" color="success" @click="save" :loading="loading">
                    {{ isNew ? 'Create' : 'Save' }}
                  </v-btn>
                </v-col>
              </v-row>

              <!-- Main Content Layout -->
              <v-row class="mr-3 ml-3">
                <!-- Left Column -->
                <v-col cols="12" md="6">
                  <!-- Membership Section (Admin Only) -->
                  <div v-if="isAdmin" class="mb-4">
                    <FletrixSelect
                      v-model="formData.membership"
                      :items="membershipOptions"
                      label="Membership"
                    />
                  </div>

                  <!-- Images Section -->
                  <div class="mb-4">
                    <custom-label itemFieldName="Clinic Images" />
                    <v-card outlined height="200">
                      <div class="d-flex align-center justify-center fill-height">
                        <div v-if="firstImageUrl" class="position-relative w-100 fill-height">
                          <v-img :src="firstImageUrl" height="200" contain />
                        </div>
                        <div v-else class="d-flex flex-column align-center">
                          <v-icon size="60" color="grey lighten-2" class="mb-2"
                            >mdi-image-multiple</v-icon
                          >
                          <p class="text-body-2 text--secondary">No images uploaded</p>
                        </div>
                      </div>
                    </v-card>
                    <v-tooltip bottom :disabled="!isNew">
                      <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on">
                          <v-btn
                            class="mt-2"
                            color="primary"
                            @click="openImagesDialog"
                            :disabled="isNew"
                          >
                            <v-icon left>mdi-image-edit</v-icon>
                            Manage Images
                          </v-btn>
                        </span>
                      </template>
                      <span>Create the clinic profile first to add images</span>
                    </v-tooltip>
                    <span
                      v-if="formData.images.length > 0"
                      class="ml-2 text-caption text--secondary"
                    >
                      {{ formData.images.length }}/5 images
                    </span>
                  </div>
                  <!-- Clinic Name -->
                  <div class="d-flex flex-column flex-gap mb-4">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="Clinic Name (EN)"
                      rules="required"
                    >
                      <FletrixTextField
                        v-model="formData.name.en"
                        :error="!!errors.length"
                        :error-messages="errors"
                        label="*Clinic Name (EN)"
                      />
                    </ValidationProvider>
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="Clinic Name (ZH-HK)"
                      rules="required"
                    >
                      <FletrixTextField
                        v-model="formData.name['zh-hk']"
                        :error="!!errors.length"
                        :error-messages="errors"
                        label="*Clinic Name (ZH-HK)"
                      />
                    </ValidationProvider>
                    <ValidationProvider
                      v-if="false"
                      v-slot="{ errors }"
                      name="Clinic Name (ZH-CN)"
                      rules="required"
                    >
                      <FletrixTextField
                        v-model="formData.name['zh-cn']"
                        :error="!!errors.length"
                        :error-messages="errors"
                        label="*Clinic Name (ZH-CN)"
                      />
                    </ValidationProvider>

                    <!-- Area Section -->
                    <ValidationProvider v-slot="{ errors }" name="Area" rules="required">
                      <FletrixSelect
                        v-model="formData.area"
                        :items="DistrictsOptions"
                        :error="!!errors.length"
                        :error-messages="errors"
                        label="*Area"
                        item-text="label"
                        item-value="value"
                      />
                    </ValidationProvider>

                    <!-- GPS Location -->
                    <ValidationProvider v-slot="{ errors }" name="GPS Location" rules="required">
                      <FletrixTextField
                        v-model="geoLocationDisplay"
                        label="GPS Location"
                        readonly
                        @click="openLocationSearchDialog"
                        :error="!!errors.length"
                        :error-messages="errors"
                      >
                        <template v-slot:append>
                          <v-icon @click.stop="openLocationSearchDialog">mdi-pencil</v-icon>
                          <v-icon
                            v-if="formData.geoLocation && formData.geoLocation.lat"
                            class="ml-1"
                            @click.stop="openGoogleMaps"
                          >
                            mdi-open-in-new
                          </v-icon>
                        </template>
                      </FletrixTextField>
                    </ValidationProvider>

                    <!-- Contact Numbers -->
                    <div class="d-flex align-center mb-2">
                      <custom-label itemFieldName="Contact Numbers" />
                      <template v-if="isAdmin">
                        <v-spacer></v-spacer>
                        <v-btn class="icon-button primary" color="primary" @click="addContact">
                          <v-icon size="32">mdi-plus</v-icon>
                        </v-btn>
                      </template>
                    </div>
                    <div
                      v-for="(contact, index) in formData.contacts"
                      :key="`contact-${index}`"
                      class="mb-3"
                    >
                      <div class="d-flex align-center">
                        <!-- <div class="text-body-2 mb-1 mr-2" style="min-width: 100px">
                        Contact No. {{ index + 1 }}
                      </div> -->
                        <FletrixPhone
                          v-model="contact.phone"
                          :disabled="!isAdmin"
                          :error-messages="errors"
                          :error="!!errors.length"
                        />
                        <v-btn
                          v-if="formData.contacts.length > 1 && isAdmin"
                          icon
                          color="error"
                          @click="removeContact(index)"
                          class="ml-auto"
                        >
                          <v-icon>mdi-delete</v-icon>
                        </v-btn>
                      </div>
                    </div>

                    <!-- Enable WhatsApp Notification -->
                    <div class="d-flex align-center mb-2">
                      <custom-label itemFieldName="WhatsApp Notification" />
                    </div>
                    <div class="mb-3">
                      <div class="d-flex align-center">
                        <v-switch
                          v-model="formData.enableWhatsAppNotification"
                          :disabled="!isAdmin"
                          label="Enable WhatsApp notification"
                          hide-details
                          dense
                        />
                      </div>
                    </div>

                    <!-- WhatsApp Number -->
                    <div class="d-flex align-center mb-2 mt-4">
                      <custom-label itemFieldName="WhatsApp Number" />
                    </div>
                    <div class="mb-3">
                      <div class="d-flex align-center">
                        <FletrixPhone
                          v-model="formData.whatsappNumber"
                          :disabled="!isAdmin"
                          placeholder="Enter WhatsApp number"
                        />
                      </div>
                    </div>
                  </div>

                  <v-divider class="my-4" />
                  <h3>Service</h3>
                  <!-- Fee Description -->
                  <div class="mb-4">
                    <FletrixTextField
                      v-model="formData.feeDescription"
                      label="Fee"
                      placeholder=""
                    />
                  </div>

                  <!-- Service Products -->
                  <div class="mb-4">
                    <ValidationProvider v-slot="{ errors }" name="Service Type" rules="required">
                      <ServiceProductSelector
                        label="Service Type"
                        v-model="formData.serviceProducts"
                        :error="!!errors.length"
                        :error-messages="errors"
                        @error="handleServiceProductError"
                      />
                    </ValidationProvider>
                  </div>
                </v-col>

                <!-- Right Column -->
                <v-col cols="12" md="6">
                  <!-- Address Section -->
                  <div class="d-flex flex-column flex-gap mb-4">
                    <ValidationProvider v-slot="{ errors }" name="*Address (EN)" rules="required">
                      <FletrixTextarea
                        v-model="formData.address.en"
                        :error="!!errors.length"
                        :error-messages="errors"
                        label="Address (EN)"
                        rows="3"
                      />
                    </ValidationProvider>
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="*Address (ZH-HK)"
                      rules="required"
                    >
                      <FletrixTextarea
                        v-model="formData.address['zh-hk']"
                        :error="!!errors.length"
                        :error-messages="errors"
                        label="Address (ZH-HK)"
                        rows="3"
                      />
                    </ValidationProvider>
                    <ValidationProvider
                      v-if="false"
                      v-slot="{ errors }"
                      name="*Address (ZH-CN)"
                      rules="required"
                    >
                      <FletrixTextarea
                        v-model="formData.address['zh-cn']"
                        :error="!!errors.length"
                        :error-messages="errors"
                        label="Address (ZH-CN)"
                        rows="3"
                      />
                    </ValidationProvider>
                    <!-- Travel Instructions Section -->
                    <FletrixTextarea
                      v-model="formData.travelInstructions.en"
                      label="How to get there (EN)"
                      rows="3"
                      placeholder="eg. 5 mins walk from Exit A2, Diamond Hill TRM Station"
                    />
                    <FletrixTextarea
                      v-model="formData.travelInstructions['zh-hk']"
                      label="How to get there (ZH-HK)"
                      rows="3"
                      placeholder="例如：鑽石山A2出口直行5分鐘"
                    />
                    <FletrixTextarea
                      v-if="false"
                      v-model="formData.travelInstructions['zh-cn']"
                      label="How to get there (ZH-CN)"
                      rows="3"
                      placeholder="例如：钻石山A2出口直行5分钟"
                    />

                    <!-- Description Section -->
                    <FletrixTextarea
                      v-model="formData.description.en"
                      label="Description (EN) ( Max:500 words )"
                      rows="3"
                      placeholder="Enter clinic description in English"
                      counter="500"
                      :rules="[
                        v => !v || v.length <= 500 || 'Description must be 500 words or less',
                      ]"
                    />
                    <FletrixTextarea
                      v-model="formData.description['zh-hk']"
                      label="Description (ZH-HK) ( Max:500 words )"
                      rows="3"
                      placeholder="輸入診所描述"
                      counter="500"
                      :rules="[
                        v => !v || v.length <= 500 || 'Description must be 500 words or less',
                      ]"
                    />
                    <FletrixTextarea
                      v-if="false"
                      v-model="formData.description['zh-cn']"
                      label="Description (ZH-CN) ( Max:500 words )"
                      rows="3"
                      placeholder="输入诊所描述"
                      counter="500"
                      :rules="[
                        v => !v || v.length <= 500 || 'Description must be 500 words or less',
                      ]"
                    />

                    <!-- Remarks Section -->
                    <FletrixTextarea
                      v-model="formData.remarks.en"
                      label="Remarks (EN) ( Max:500 words )"
                      rows="3"
                      placeholder="eg. Health Care Voucher is accepted, Colon screening, Free flu vaccination"
                      counter="500"
                      :rules="[v => !v || v.length <= 500 || 'Remarks must be 500 words or less']"
                    />
                    <FletrixTextarea
                      v-model="formData.remarks['zh-hk']"
                      label="Remarks (ZH-HK) ( Max:500 words )"
                      rows="3"
                      placeholder="例如：接受醫療券、大腸篩查、免費流感疫苗接種。"
                      counter="500"
                      :rules="[v => !v || v.length <= 500 || 'Remarks must be 500 words or less']"
                    />
                    <FletrixTextarea
                      v-if="false"
                      v-model="formData.remarks['zh-cn']"
                      label="Remarks (ZH-CN) ( Max:500 words )"
                      rows="3"
                      placeholder="例如：接受医疗券、大肠筛查、免费流感疫苗接种。"
                      counter="500"
                      :rules="[v => !v || v.length <= 500 || 'Remarks must be 500 words or less']"
                    />

                    <!-- Payment Methods Section -->
                    <FletrixTextarea
                      v-model="formData.paymentMethods"
                      label="Payment Methods"
                      rows="3"
                      placeholder="eg. Cash, Master, VISA, FPS, Octopus."
                    />
                  </div>
                </v-col>
              </v-row>

              <!-- Location Search Dialog -->
              <LocationSearchDialog
                v-model="locationSearchDialog"
                :current-location="formData.geoLocation"
                @confirm="onLocationConfirmed"
                @error="handleLocationError"
              />
            </v-card>
          </ValidationObserver>
        </div>

        <!-- Opening Time Tab -->
        <div v-if="activeTab === 1 && !isNew">
          <v-card flat>
            <!-- Opening Hours Section -->
            <div class="pa-4">
              <h3 class="mb-4">Regular Opening Hours</h3>
              <WeekSchedule
                :open-hours="formData.openHours"
                :break-hours="formData.breakHours"
                :clinic-id="id"
                status-label="Status"
                active-label="Open"
                inactive-label="Closed"
                inactive-chip-label="Closed"
                @update="saveOpeningTimeData"
                @update-breaks="updateBreakHours"
                :enableBreakSetting="true"
              />

              <!-- Opening Time Remarks -->
              <v-row class="mt-4">
                <v-col cols="12">
                  <FletrixTextarea
                    v-model="formData.openingTimeRemarks"
                    flat
                    label="Opening Time Remarks"
                    outlined
                    rows="3"
                    placeholder="Enter any special notes about opening hours"
                  />
                </v-col>
              </v-row>
            </div>

            <v-divider class="my-4" />

            <!-- Holidays Section -->
            <div class="pa-4">
              <h3 class="mb-4">Clinic Holidays</h3>
              <ClinicHolidays :clinic-id="id" />
            </div>
          </v-card>
        </div>

        <!-- Doctors Tab -->
        <div v-if="activeTab === 2 && !isNew">
          <v-card flat>
            <ClinicDoctors :clinic-id="id" />
          </v-card>
        </div>

        <!-- Staff Tab -->
        <div v-if="activeTab === 3 && !isNew">
          <v-card flat>
            <ClinicStaff :clinic-id="id" />
          </v-card>
        </div>
      </div>
    </v-container>
    <!-- Images Management Dialog -->
    <v-dialog
      v-model="fileGrid.dialogVisible"
      max-width="900"
      persistent
      :loading="fileGrid.isProcessing"
    >
      <v-card :loading="fileGrid.isProcessing">
        <v-card-title class="primaryDialogTitle">
          <span class="headline">Manage Clinic Images</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="fileGridCloseDialog" :disabled="fileGrid.isProcessing">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div class="d-flex align-center justify-space-between mb-3">
            <div>
              <div class="text-caption text--secondary">
                Select up to 5 images for your clinic (JPEG, PNG, JPG)
              </div>
              <div class="text-caption">
                <v-chip small :color="!fileGridCanAddMore ? 'error' : 'primary'" class="mt-1">
                  {{ fileGridDocumentCount }}/{{ fileGrid.maxFiles }} images
                </v-chip>
              </div>
            </div>
            <v-btn
              v-if="fileGridCanAddMore"
              color="primary"
              @click="$refs.imageUploader.click()"
              :disabled="fileGrid.isProcessing"
            >
              <v-icon left>mdi-plus</v-icon>
              Add Images
            </v-btn>
            <input
              ref="imageUploader"
              multiple
              :accept="fileGrid.acceptTypes"
              class="d-none"
              type="file"
              @click="$event.target.value = ''"
              @change="fileGridOnFilesSelected"
            />
          </div>

          <FileGrid
            v-if="fileGridDocumentCount > 0"
            :value="fileGrid.tempDocuments"
            @remove="fileGridRemoveDocument"
            @select="fileGridSelectDocument"
            item-size="200px"
            class="mb-3"
          />
          <div v-else class="text-center py-8">
            <v-icon size="60" color="grey lighten-2">mdi-image-multiple</v-icon>
            <p class="text-body-2 text--secondary mt-2">No images selected</p>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="fileGridCloseDialog" :disabled="fileGrid.isProcessing">Cancel</v-btn>
          <v-btn
            color="primary"
            @click="saveImages"
            :loading="fileGrid.isProcessing"
            :disabled="fileGrid.isProcessing"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { RESTFUL } from '@/data/constants';
import api from '@/api';
import { mapActions, mapGetters } from 'vuex';
import { membershipOptions, DistrictsOptions } from '@/data/optionsConstants';
import WeekSchedule from '@/components/ClinicProfile/WeekSchedule';
import ClinicHolidays from '@/components/ClinicProfile/ClinicHolidays';
import ClinicDoctors from '@/components/ClinicProfile/ClinicDoctors';
import ClinicStaff from '@/components/ClinicProfile/ClinicStaff';
import ServiceProductSelector from '@/components/ClinicProfile/ServiceProductSelector';
import FileGrid from '@/components/FileGrid';
import FileGridMixin from '@/mixins/FileGridMixin';
import { getThumbnail } from '@/services/filters';
import LocationSearchDialog from '@/components/LocationSearchDialog';
import { FletrixTextField, FletrixTextarea, FletrixSelect, FletrixPhone } from '@/components/Atom';
import customLabel from '@/components/customFormLabel/customLabel';
import HeaderLabel from '@/components/customFormLabel/headerLabel.vue';
export default {
  name: 'ClinicProfileDetails',
  components: {
    WeekSchedule,
    ClinicHolidays,
    ClinicDoctors,
    ClinicStaff,
    ServiceProductSelector,
    FileGrid,
    LocationSearchDialog,
    FletrixTextField,
    FletrixTextarea,
    FletrixSelect,
    FletrixPhone,
    customLabel,
    HeaderLabel,
  },
  mixins: [FileGridMixin],
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      activeTab: 0,
      formData: {
        building: '',
        geoLocation: {},
        membership: 'default',
        area: null,
        name: {
          en: '',
          'zh-hk': '',
          'zh-cn': '',
        },
        address: {
          en: '',
          'zh-hk': '',
          'zh-cn': '',
        },
        travelInstructions: {
          en: '',
          'zh-hk': '',
          'zh-cn': '',
        },
        contacts: [{ phone: '' }],
        whatsappNumber: '',
        enableWhatsAppNotification: false,
        openHours: [],
        breakHours: [],
        openingTimeRemarks: '',
        serviceProducts: [],
        description: {
          en: '',
          'zh-hk': '',
          'zh-cn': '',
        },
        feeDescription: '',
        paymentMethods: '',
        remarks: {
          en: '',
          'zh-hk': '',
          'zh-cn': '',
        },
        images: [],
      },
      clinicData: null,
      loading: false,
      api: api, // Make api available to mixin
      locationSearchDialog: false,
      membershipOptions,
      DistrictsOptions,
    };
  },
  computed: {
    ...mapGetters('auth', ['isAdmin']),
    isNew() {
      return this.id === 'new';
    },
    tabs() {
      const tabsConfig = [
        {
          id: 0,
          label: 'Details',
          show: true,
        },
        {
          id: 1,
          label: 'Opening Time',
          show: !this.isNew,
        },
        {
          id: 2,
          label: 'Doctors',
          show: !this.isNew,
        },
        {
          id: 3,
          label: 'Staff',
          show: !this.isNew,
        },
      ];
      return tabsConfig.filter(tab => tab.show);
    },
    firstImageUrl() {
      // Show the first existing image
      if (this.formData.images && this.formData.images.length > 0) {
        return getThumbnail(api, this.formData.images[0], 512);
      }
      return null;
    },
    geoLocationDisplay() {
      if (this.formData.geoLocation && this.formData.geoLocation.lat) {
        return `${this.formData.geoLocation.lat.toFixed(
          6,
        )}, ${this.formData.geoLocation.lng.toFixed(6)}`;
      }
      return '';
    },
  },
  async mounted() {
    if (!this.isNew) {
      await this.reload();
    }
    // Initialize file grid with existing images
    this.fileGrid.originalIds = this.formData.images || [];
  },
  methods: {
    ...mapActions('alert', ['updateAlertMessage']),

    // Helper function to parse i18n fields
    parseI18nField(fieldValue, defaultStructure) {
      if (!fieldValue) return defaultStructure;

      try {
        const parsedValue = typeof fieldValue === 'string' ? JSON.parse(fieldValue) : fieldValue;
        return { ...defaultStructure, ...parsedValue };
      } catch (e) {
        console.error('Error parsing i18n field:', e);
        // If it's a plain string, set it as English
        return { ...defaultStructure, en: fieldValue };
      }
    },

    async reload() {
      try {
        this.loading = true;
        const response = await api.get(`${RESTFUL.clinicProfile.read}/${this.id}`);
        this.clinicData = response.data;
        this.parseClinicData();
      } catch (error) {
        console.error('Error loading clinic data:', error);
        await this.updateAlertMessage({
          msg: 'Failed to load clinic profile',
          type: 'error',
        });
      } finally {
        this.loading = false;
      }
    },

    parseClinicData() {
      if (!this.clinicData) return;

      // Define default i18n structure
      const i18nDefault = { en: '', 'zh-hk': '', 'zh-cn': '' };

      // Parse i18n fields using the helper function
      this.formData.name = this.parseI18nField(this.clinicData.name, i18nDefault);
      this.formData.address = this.parseI18nField(this.clinicData.address, i18nDefault);
      this.formData.travelInstructions = this.parseI18nField(
        this.clinicData.travelInstructions,
        i18nDefault,
      );
      this.formData.description = this.parseI18nField(this.clinicData.description, i18nDefault);
      this.formData.remarks = this.parseI18nField(this.clinicData.remarks, i18nDefault);

      // Parse contacts - PhoneValidator expects full phone with country code
      if (this.clinicData.contacts && Array.isArray(this.clinicData.contacts)) {
        this.formData.contacts = this.clinicData.contacts.map(contact => ({
          phone: contact.phone || '',
        }));
      } else {
        // Ensure at least one contact field if no contacts exist
        this.formData.contacts = [{ phone: '' }];
      }

      this.formData.area = this.clinicData.area || null;
      this.formData.membership = this.clinicData.membership || 'default';
      this.formData.whatsappNumber = this.clinicData.whatsappNumber || '';
      this.formData.enableWhatsAppNotification =
        this.clinicData.enableWhatsAppNotification || false;
      this.formData.openHours = this.clinicData.openHours || [];
      this.formData.breakHours = this.clinicData.breakHours || [];
      this.formData.openingTimeRemarks = this.clinicData.openingTimeRemarks || '';
      this.formData.serviceProducts = this.clinicData.serviceProducts || [];
      this.formData.feeDescription = this.clinicData.feeDescription || '';
      this.formData.paymentMethods = this.clinicData.paymentMethods || '';
      this.formData.geoLocation = this.clinicData.geoLocation || {};
      this.formData.images = this.clinicData.images || [];

      // Update file grid with loaded images
      this.fileGrid.originalIds = this.formData.images || [];
    },

    async save() {
      const isValid =
        this.$refs.form && this.$refs.form.validate ? await this.$refs.form.validate() : false;
      if (!isValid) {
        return;
      }

      if (this.formData.enableWhatsAppNotification && !this.formData.whatsappNumber) {
        await this.updateAlertMessage({
          msg: 'WhatsApp number is required when WhatsApp notification is enabled',
          type: 'error',
        });
        return;
      }

      try {
        this.loading = true;

        const payload = {
          name: JSON.stringify(this.formData.name),
          address: JSON.stringify(this.formData.address),
          travelInstructions: JSON.stringify(this.formData.travelInstructions),
          description: JSON.stringify(this.formData.description),
          remarks: JSON.stringify(this.formData.remarks),
          contacts: this.formData.contacts
            .filter(contact => contact.phone)
            .map(contact => ({
              phone: contact.phone,
            })),
          whatsappNumber: this.formData.whatsappNumber || null,
          enableWhatsAppNotification: this.formData.enableWhatsAppNotification || false,
          area: this.formData.area,
          membership: this.formData.membership,
          openHours: this.formData.openHours,
          serviceProducts: this.formData.serviceProducts,
          feeDescription: this.formData.feeDescription,
          paymentMethods: this.formData.paymentMethods,
          geoLocation: this.formData.geoLocation,
          images: this.formData.images,
        };

        if (this.isNew) {
          await api.post(RESTFUL.clinicProfile.create, payload);
          await this.updateAlertMessage({
            msg: 'Clinic profile created successfully',
            type: 'success',
            color: 'success',
          });
          this.$router.push({ name: 'clinicProfiles' });
        } else {
          await api.patch(`${RESTFUL.clinicProfile.read}/${this.id}`, payload);
          await this.updateAlertMessage({
            msg: 'Clinic profile updated successfully',
            type: 'success',
            color: 'success',
          });
          await this.reload();
        }
      } catch (error) {
        console.error('Error saving clinic data:', error);
        await this.updateAlertMessage({
          msg: `Failed to ${this.isNew ? 'create' : 'update'} clinic profile`,
          type: 'error',
        });
      } finally {
        this.loading = false;
      }
    },

    addContact() {
      this.formData.contacts.push({ phone: '' });
    },

    removeContact(index) {
      if (this.formData.contacts.length > 1) {
        this.formData.contacts.splice(index, 1);
      }
    },

    updateBreakHours(breakHours) {
      this.formData.breakHours = breakHours;
    },

    async saveOpeningTimeData(openHours) {
      // Update the openHours when save is triggered from WeekSchedule
      if (openHours) {
        this.formData.openHours = openHours;
      }
      try {
        this.loading = true;

        // Save the updated opening hours, break hours and remarks to the server
        const payload = {
          openHours: this.formData.openHours,
          breakHours: this.formData.breakHours,
          openingTimeRemarks: this.formData.openingTimeRemarks,
        };

        await api.patch(`${RESTFUL.clinicProfile.read}/${this.id}`, payload);
        await this.updateAlertMessage({
          msg: 'Opening time information saved successfully',
          type: 'success',
          color: 'success',
        });
        await this.reload();
      } catch (error) {
        console.error('Error saving opening time data:', error);
        await this.updateAlertMessage({
          msg: 'Failed to save opening time information',
          type: 'error',
        });
      } finally {
        this.loading = false;
      }
    },

    handleServiceProductError(message) {
      this.updateAlertMessage({
        msg: message,
        type: 'error',
      });
    },

    openImagesDialog() {
      // Initialize file grid with current images
      this.fileGridInitialize(this.formData.images, {
        maxFiles: 5,
        acceptTypes: 'image/jpeg,image/png,image/jpg',
      });
      this.fileGrid.dialogVisible = true;
    },

    async saveImages() {
      try {
        // Set loading state
        this.fileGrid.isProcessing = true;

        // Prepare pending files and removed IDs (without closing dialog)
        this.fileGrid.pendingFiles = this.fileGrid.tempDocuments
          .filter(doc => doc.isNew)
          .map(doc => doc.file);

        const currentExistingIds = this.fileGrid.tempDocuments
          .filter(doc => doc.isExisting)
          .map(doc => doc._id);

        this.fileGrid.removedIds = this.fileGrid.originalIds.filter(
          id => !currentExistingIds.includes(id),
        );

        // Check if there are any changes
        if (!this.fileGridHasChanges) {
          await this.updateAlertMessage({
            msg: 'No changes to save',
            type: 'info',
          });

          this.fileGrid.dialogVisible = false;
          return;
        }

        // Process image changes (upload new, remove old)
        const finalImageIds = await this.fileGridProcessChanges();

        // Update the backend with new image IDs
        const payload = {
          images: finalImageIds,
        };

        await api.patch(`${RESTFUL.clinicProfile.read}/${this.id}`, payload);

        // Update local formData
        this.formData.images = finalImageIds;

        await this.updateAlertMessage({
          msg: 'Images saved successfully',
          type: 'success',
          color: 'success',
        });

        // Close dialog and reload to refresh the view
        this.fileGrid.dialogVisible = false;
        await this.reload();
      } catch (error) {
        console.error('Error saving images:', error);
        await this.updateAlertMessage({
          msg: 'Failed to save images',
          type: 'error',
        });
      } finally {
        // Always reset loading state
        this.fileGrid.isProcessing = false;
      }
    },

    openLocationSearchDialog() {
      this.locationSearchDialog = true;
    },

    openGoogleMaps() {
      if (this.formData.geoLocation && this.formData.geoLocation.lat) {
        window.open(
          `https://www.google.com/maps?q=${this.formData.geoLocation.lat},${this.formData.geoLocation.lng}`,
          '_blank',
        );
      }
    },

    onLocationConfirmed(location) {
      this.formData.geoLocation = { ...location };
    },

    handleLocationError(message) {
      this.updateAlertMessage({
        msg: message,
        type: 'error',
      });
    },
  },
};
</script>

<style scoped lang="scss">
.tab-buttons-container {
  display: flex;
  margin-bottom: 16px;
  padding: 8px;
}

.tab-content {
  min-height: 400px;
}
</style>
